import { SelectItem } from '@/types/ui';
import { getItemByKey } from '@/utils/ui';
import { getDeepestHierarchyLevel } from './hierarchyPath';

export function getSingleUserCountryLabel(
  userCountries: SelectItem<string>[] = [],
  assignedCountryCodes: string[] = [],
): string | undefined {
  if (assignedCountryCodes.length !== 1 || userCountries.length === 0) return undefined;

  const code = assignedCountryCodes[0];
  const found = userCountries.find(country => country.value === code);
  if (!found || typeof found.label !== 'string') return undefined;

  return found.label;
}

export const extractCountryName = (value: string) => getDeepestHierarchyLevel(value);

/**
 * Resolves the country codes for a selected hierarchical path.
 * If a leaf is selected, returns its code.
 * If a parent is selected, expands to all descendant leaves.
 *
 * O(n) complexity — scans all options once.
 */
export function resolveCountryCodes(
  selectedPath: string,
  allOptions: { value: string }[],
  userCountries: SelectItem<string>[],
): string[] {
  if (!allOptions.length || !userCountries.length) return [];

  const countryNameToCode = new Map(userCountries.map(country => [country.label, country.value]));

  const nonLeafPrefixes = new Set<string>();

  for (const option of allOptions) {
    const parts = option.value.split('__');
    for (let i = 1; i < parts.length; i++) {
      nonLeafPrefixes.add(parts.slice(0, i).join('__'));
    }
  }

  const leafPaths = new Set(allOptions.map(o => o.value).filter(leafItem => !nonLeafPrefixes.has(leafItem)));

  const expandedCodes: string[] = [];
  for (const leafPath of leafPaths) {
    if (leafPath === selectedPath || leafPath.startsWith(selectedPath + '__')) {
      const code = countryNameToCode.get(extractCountryName(leafPath));
      if (code) expandedCodes.push(code);
    }
  }

  return expandedCodes;
}

/**
 * Resolve a human-readable country label from ISO codes, tree-path filter
 * values, or fall back to the "all countries" translation.
 */
export const resolveCountryLabel = (
  codes: string[],
  userCountries: SelectItem<string>[],
  t: (key: string) => string,
  countryFilterValues?: string[],
): string => {
  // Prefer country filter tree values — they contain the full list of
  // selected country names and don't depend on the recipient's available
  // countries (e.g. a DE-local user can't resolve 'DZ' or 'NG' codes).
  if (countryFilterValues?.length) {
    return countryFilterValues.map(extractCountryName).join(', ');
  }
  // Fallback: resolve from ISO codes
  if (codes.length) {
    const names = codes.map(code => getItemByKey(code, userCountries)?.label).filter(Boolean);
    if (names.length > 0) return names.join(', ');
  }
  return t('global.filters.sharedViewDialog.allCountries');
};
